// Shared base URL for the backend endpoints (/tts, /lib).
//
// In production the SPA is served behind a proxy that routes those
// paths itself, so every request stays relative to the current origin.
// During local development (`npm run dev`) there is no proxy — set
// VITE_REMOTE_BASE in .env (e.g. https://reader.example.com) to point
// the dev server at a running deployment. Empty (the default) keeps
// all requests relative.

export const REMOTE_BASE: string =
  (import.meta.env.VITE_REMOTE_BASE as string | undefined) ?? "";

// Public-deployment mode: build with VITE_PUBLIC_MODE=1 to fully disable
// the cloud library client (/lib), even if a /lib endpoint is reachable on
// the same origin. The public site then seeds the bundled public-domain
// starter library instead of syncing anyone's personal shelf.
export const PUBLIC_MODE: boolean =
  ((import.meta.env.VITE_PUBLIC_MODE as string | undefined) ?? "") === "1";

/** Prefix `path` with REMOTE_BASE whenever the app is served from an
 *  origin other than REMOTE_BASE itself (localhost dev, the Tailscale
 *  hub, etc). When the app is served from REMOTE_BASE's own origin
 *  (e.g. the public deploy at https://1n2.org) the bare relative path
 *  is kept so the host's own /lib and /tts proxies handle routing.
 *
 *  2026-07-10: previously only localhost was prefixed, which made the
 *  hub copy at curiomini:8800 send /lib requests to serve.py (404) —
 *  the cloud library looked unreachable and the starter seed fired on
 *  fresh profiles, masking the owner's personal library. */
export function remoteUrl(path: string): string {
  if (!REMOTE_BASE) return path;
  if (typeof window === "undefined") return path;
  try {
    const base = new URL(REMOTE_BASE);
    if (window.location.origin === base.origin) return path;
  } catch {
    return path;
  }
  return `${REMOTE_BASE}${path}`;
}
